<style scoped>
.shimmerElement {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>

<template>
  <div class="">
    <VBusHelper :emitOnMounted="$constants.EVENTS.MATCH.LIVE.REFRESH" />
    <div class="flex flex-col">
      <template v-if="$stores.match.state?.live?.loading">
        <!-- Simuler plusieurs blocs de chargement -->
        <div v-for="i in 5" :key="`shimmer-${i}`" class="scroll-m-[9rem] pb-32">
          <!-- Matches shimmer blocks -->
          <div class="space-y-4">
            <!-- Simuler 2-3 cartes de match par jour -->
            <div
              v-for="j in 2"
              :key="`shimmer-match-${i}-${j}`"
              class="overflow-hidden"
            >
              <div class="p-4 space-y-3">
                <!-- Header catégorie shimmer -->
                <div class="flex items-center gap-4">
                  <div class="w-8 h-8 shimmerElement rounded-full"></div>
                  <div class="flex-1 space-y-2">
                    <div class="w-48 h-4 shimmerElement rounded-lg"></div>
                    <div class="w-32 h-3 shimmerElement rounded-full"></div>
                  </div>
                  <div class="w-5 h-5 shimmerElement rounded-full"></div>
                </div>

                <!-- Matches shimmer -->
                <div class="space-y-2 pt-2">
                  <div
                    v-for="k in 2"
                    :key="`shimmer-team-${i}-${j}-${k}`"
                    class="flex items-center gap-3"
                  >
                    <div class="w-12 h-4 shimmerElement rounded-full"></div>
                    <div class="flex-1">
                      <div
                        class="w-24 h-3 shimmerElement rounded-full mb-1"
                      ></div>
                      <div class="w-16 h-3 shimmerElement rounded-full"></div>
                    </div>
                    <div class="w-8 h-4 shimmerElement rounded-full"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <template v-else>
        <div class="pb-32">
          <!-- Contenu : matches ou message "pas de match" -->
          <div
            v-if="$stores.match.state?.live?.list?.[0]?.dayMatches?.length > 0"
          >
            <div
              v-for="(categoryData, categoryIndex) in ($stores.match.state?.live
                ?.list?.[0]?.dayMatches || []).slice(0, 1)"
              :key="categoryIndex"
              class="mb-5"
            >
              <div
                class="bg-white overflow-hidden"
                @click="
                  $bus.emit(
                    $constants.EVENTS.MATCH.SHOW_COMPETITION_DETAIL,
                    categoryData
                  )
                "
              >
                <!-- Header catégorie -->
                <div
                  class="flex bg-[#E9EEF2] items-center overflow-hidden gap-5 p-4"
                >
                  <div class="w-8 h-8 min-w-8">
                    <ImgComponent
                      :src="categoryData.category.logo"
                      fallback="/images/match/terrain.svg"
                      class="w-full h-full"
                      alt=""
                    />
                  </div>
                  <div class="w-full overflow-hidden">
                    <div
                      class="truncate w-full text-lg font-bold text-primary-800"
                    >
                      {{ categoryData.category.name }}
                    </div>
                    <div class="truncate w-full text-primary-600">
                      {{ categoryData.category.round }}
                    </div>
                  </div>
                  <div class="w-5 h-5 min-w-5 flex items-center justify-center">
                    <ColorImg
                      src="/images/icons/right-arrow.svg"
                      class="w-full h-full bg-gray-500"
                      alt=""
                    />
                  </div>
                </div>

                <!-- Matches de la catégorie -->
                <div class="divide-y divide-gray-200">
                  <MatchesPresentation
                    v-for="(match, matchIndex) in categoryData.matches"
                    :key="matchIndex"
                    :match="match"
                    @click.stop="
                      $bus.emit(
                        $constants.EVENTS.MATCH.SHOW_MATCH_DETAIL,
                        match
                      )
                    "
                  />
                </div>
              </div>
              <div v-if="categoryIndex == 0">
                <div class="py-4 bg-[#E9EEF2] rounded-xl my-6">
                  <div class="md:mb-6 flex justify-between px-4">
                    <div class="flex items-center gap-2 md:gap-3">
                      <ColorImg
                        src="/images/navs/pronostics.svg"
                        class="w-6 h-6 md:w-7 md:h-7 bg-current text-[#070C4C]"
                      />
                      <div class="text-[#070C4C] text-lg md:text-xl font-bold">
                        Pronostics
                      </div>
                    </div>
                    <router-link
                      class="flex text-[#070C4C] items-center gap-2"
                      to="/pronostics"
                    >
                      <div>Voir</div>
                      <ColorImg
                        src="/images/icons/right-arrow.svg"
                        class="w-4 h-4 text-[#070C4C] bg-current -mt-1"
                      />
                    </router-link>
                  </div>
                  <div class="w-full overflow-auto relative px-4 flex mt-3">
                    <PronosticMatchs class="" shortList></PronosticMatchs>
                    <div class="w-5"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Message pour les jours sans match -->
          <div v-else class="px-4">
            <div
              class="border-2 border-dashed border-gray-300 bg-gray-50/30 p-12 text-center"
            >
              <div class="flex flex-col items-center justify-center space-y-4">
                <!-- Icône de calendrier ou match -->
                <div
                  class="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center"
                >
                  <svg
                    class="w-8 h-8 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>

                <!-- Texte explicatif -->
                <div class="space-y-2">
                  <h3 class="text-lg font-semibold text-gray-700">
                    Aucun match ce jour
                  </h3>
                  <p class="text-gray-500 text-sm max-w-xs mx-auto">
                    Les compétitions reprennent demain avec de nouveaux
                    affrontements passionnants !
                  </p>
                </div>

                <!-- Indicateur visuel -->
                <div class="flex items-center space-x-2 text-xs text-gray-400">
                  <svg
                    class="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span>Journée de repos</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>
